import React from 'react'
import styled from 'styled-components'
import { EditOutlined } from '@material-ui/icons'

import { strings } from '@constants'
import { Address } from '@data/model'
import { Spacings, Text, Devices } from '@styles'

import Card from '../card'
import CardHeaderV2 from '../cardHeader'
import Button from '../../basic/Button'
import Pill from '../../basic/Pill'

const StyledCard = styled(Card)`
	height: 100%;
`

const AddressInnerContainer = styled.div`
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.md};
	padding: ${Spacings.SPACING_3B};
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_6};
	min-height: calc(10 * ${Spacings.SPACING_11});
	height: 100%;

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_4B};
		gap: ${Spacings.SPACING_2B};
	}
`

const AddressIDLabel = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
	font-weight: ${Text.BOLD_500};
	${({ onClick }) => onClick && `cursor: copy;`}
`

const AddressText = styled.p`
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.primary};
	margin: 0;
	line-height: 1.5;

	@media ${Devices.tablet} {
		font-size: ${Text.MEDIUM};
		line-height: 1.6;
	}
`

const AddressEmptyContainer = styled(AddressInnerContainer)`
	align-items: center;
	justify-content: center;
	text-align: center;
`

const AddressEmptyText = styled(AddressText)`
	color: ${({ theme }) => theme.colors.text.secondary};
`

const GstRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: ${Spacings.SPACING_2B};
	width: 100%;
`
const TradeNameChip = styled.div`
	display: inline-flex;
	align-items: center;
	gap: ${Spacings.SPACING_1B};
	width: fit-content;
	padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_2B};
	border-radius: ${({ theme }) => theme.radius.sm};
	background: ${({ theme }) => theme.colors.surface.sunken};
	font-size: ${Text.TINY_SMALL};
	color: ${({ theme }) => theme.colors.text.primary};

	b {
		color: ${({ theme }) => theme.colors.text.secondary};
		font-weight: ${Text.BOLD_600};
	}
`

const AddressDetailsCard = ({
	title,
	icon: Icon,
	onEdit,
	gst,
	tradeName,
	addressData,
	fallbackText,
	extraContent,
	onEditGst,
	isAddressSelected = true,
	showSelectAddressInHeader = false,
	size,
	onCopyAddressId,
	disabled = false,
	actionLabel,
}) => {
	const showHeaderAction = onEdit && (isAddressSelected || showSelectAddressInHeader)
	const addressId = addressData?.address_id

	const gstPill = gst && (
		<Pill color='info' variant='subtle' round>
			{strings('gst', 'number')} : {gst}
		</Pill>
	)

	const gstSection = onEditGst && (
		<GstRow>
			{gstPill || <AddressIDLabel>{strings('gst', 'number')} : --</AddressIDLabel>}
			<Button variant='link' color='primary' onClick={onEditGst} disabled={disabled}>
				<EditOutlined />
				{gst ? strings('edit') : strings('add')}
			</Button>
		</GstRow>
	)

	return (
		<StyledCard hoverable={false}>
			<CardHeaderV2
				title={title}
				icon={Icon}
				showAction={showHeaderAction}
				actionLabel={actionLabel ?? (isAddressSelected ? strings('edit') : strings('add'))}
				onAction={onEdit}
				size={size}
				disabled={disabled}
			/>
			{isAddressSelected ? (
				<AddressInnerContainer>
					{!onEditGst && gstPill}
					{addressId && (
						<AddressIDLabel onClick={onCopyAddressId ? (e) => onCopyAddressId(e, addressId) : undefined}>
							{strings('address_id')} : {addressId}
						</AddressIDLabel>
					)}
					{tradeName && (
						<TradeNameChip>
							<b>{strings('trade_name')}:</b>
							{tradeName}
						</TradeNameChip>
					)}
					<AddressText>{addressData ? Address.toString(addressData) : fallbackText}</AddressText>
					{extraContent}
				</AddressInnerContainer>
			) : (
				<AddressEmptyContainer>
					<AddressEmptyText>{strings('no_address_selected')}</AddressEmptyText>
				</AddressEmptyContainer>
			)}
			{gstSection}
		</StyledCard>
	)
}

export default AddressDetailsCard
